/**
 * @file       Currency input directive for $nn,nnn.nn format
 * @version    1.0.0
 * @author     Boris Veits
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or
 *             altered in anyway without expressed written permission from
 *             MindShare HDV LLC.
 */
import
{
    Directive,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    Optional
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { formatCurrencyInput } from
    '@app/secure/helper/currencyFormat.helper';

@Directive
({
    selector: '[appCurrencyInput]',
    standalone: true
})

/**
 * @desc   - Formats money as $nn,nnn.nn while typing
 */
class CurrencyInputDirective implements OnInit, OnDestroy
{
    private updating: boolean;
    private valueSub: Subscription;

    /**
     * @desc   - directive constructor
     * @access - public
     * @param  - el : ElementRef
     * @param  - ngControl : NgControl (optional)
     * @return -
     */
    constructor
    (
        private el: ElementRef,
        @Optional() private ngControl: NgControl
    )
    {
        this.updating = false;
        this.valueSub = null;
    }

    /**
     * @desc   - Whether the host input currently has focus
     * @access - private
     * @param  -
     * @return - true when the input is focused
     */
    private isInputFocused(): boolean
    {
        let result;
        let inputElement;

        result = false;
        inputElement = this.el.nativeElement;

        if (inputElement &&
            document.activeElement === inputElement)
        {
            result = true;
        }

        return result;
    }

    /**
     * @desc   - Format the host input from its current value
     * @access - private
     * @param  - complete : pad cents when true
     * @return -
     */
    private applyElementFormat
    (
        complete: boolean
    ): void
    {
        this.applyFormatting(
            this.el.nativeElement,
            complete
        );
    }

    /**
     * @desc   - Count digits to the left of a caret
     * @access - private
     * @param  - value : current field text
     * @param  - position : caret index
     * @return - digit count before the caret
     */
    private countDigitsBefore
    (
        value: string,
        position: number
    ): number
    {
        let result;
        let i;
        let limit;
        let ch;

        result = 0;
        limit = 0;

        if (value)
        {
            limit = value.length;

            if (position < limit)
            {
                limit = position;
            }
        }

        for (i = 0; i < limit; i++)
        {
            ch = value.charAt(i);

            if (ch >= '0' && ch <= '9')
            {
                result = result + 1;
            }
        }

        return result;
    }

    /**
     * @desc   - Map a digit count back to a caret index
     * @access - private
     * @param  - formatted : formatted currency text
     * @param  - oldValue : text before format
     * @param  - oldPos : caret before format
     * @return - caret index in formatted text
     */
    private resolveCaret
    (
        formatted: string,
        oldValue: string,
        oldPos: number
    ): number
    {
        let result;
        let digitsBefore;
        let afterDot;
        let i;
        let seen;
        let ch;

        result = 0;
        digitsBefore = this.countDigitsBefore(oldValue, oldPos);
        afterDot = false;
        seen = 0;

        if (oldValue && oldPos > 0 &&
            oldValue.charAt(oldPos - 1) === '.')
        {
            afterDot = true;
        }

        if (formatted)
        {
            result = formatted.length;

            for (i = 0; i < formatted.length; i++)
            {
                ch = formatted.charAt(i);

                if (ch >= '0' && ch <= '9')
                {
                    seen = seen + 1;

                    if (!afterDot && seen === digitsBefore)
                    {
                        result = i + 1;
                    }
                }
                else if (ch === '.' && afterDot &&
                    seen === digitsBefore)
                {
                    result = i + 1;
                }
            }

            if (digitsBefore === 0 && !afterDot)
            {
                if (formatted.charAt(0) === '$')
                {
                    result = 1;
                }
                else
                {
                    result = 0;
                }
            }
        }

        return result;
    }

    /**
     * @desc   - Write formatted text back to the input
     * @access - private
     * @param  - inputElement : the amount input
     * @param  - complete : pad cents when true
     * @return -
     */
    private applyFormatting
    (
        inputElement: HTMLInputElement,
        complete: boolean
    ): void
    {
        let currentValue;
        let caret;
        let formatted;
        let nextCaret;
        let control;

        currentValue = '';
        caret = 0;
        formatted = '';
        nextCaret = 0;
        control = null;

        if (!this.updating && inputElement)
        {
            if (this.ngControl)
            {
                control = this.ngControl.control;
            }

            currentValue = inputElement.value || '';

            if (currentValue === '' && control &&
                control.value !== null &&
                control.value !== undefined &&
                control.value !== '')
            {
                currentValue = String(control.value);
            }

            caret = inputElement.selectionStart;

            if (caret === null || caret === undefined)
            {
                caret = currentValue.length;
            }

            formatted = formatCurrencyInput(
                currentValue,
                complete
            );

            if (formatted !== currentValue)
            {
                nextCaret = this.resolveCaret(
                    formatted,
                    currentValue,
                    caret
                );
                this.updating = true;
                inputElement.value = formatted;

                if (typeof inputElement.setSelectionRange ===
                    'function')
                {
                    inputElement.setSelectionRange(
                        nextCaret,
                        nextCaret
                    );
                }

                if (control)
                {
                    control.setValue(
                        formatted,
                        { emitEvent: false }
                    );
                }

                this.updating = false;
            }
        }
    }

    /**
     * @desc   - Format an existing value and watch patches
     * @access - public
     * @param  -
     * @return -
     */
    ngOnInit(): void
    {
        let control;

        control = null;
        this.applyElementFormat(true);

        if (this.ngControl)
        {
            control = this.ngControl.control;
        }

        if (control)
        {
            this.valueSub = control.valueChanges.subscribe(() =>
            {
                this.applyElementFormat(!this.isInputFocused());
            });
        }
    }

    /**
     * @desc   - Drop the value-change subscription
     * @access - public
     * @param  -
     * @return -
     */
    ngOnDestroy(): void
    {
        if (this.valueSub)
        {
            this.valueSub.unsubscribe();
        }
    }

    /**
     * @desc   - Format as the user types
     * @access - public
     * @param  - event : input event from the field
     * @return -
     */
    @HostListener('input', ['$event'])
    onInput
    (
        event: InputEvent
    ): void
    {
        let inputElement;

        inputElement = event.target as HTMLInputElement;
        this.applyFormatting(inputElement, false);
    }

    /**
     * @desc   - Pad cents when the field loses focus
     * @access - public
     * @param  - event : blur event from the field
     * @return -
     */
    @HostListener('blur', ['$event'])
    onBlur
    (
        event: FocusEvent
    ): void
    {
        let inputElement;

        inputElement = event.target as HTMLInputElement;
        this.applyFormatting(inputElement, true);
    }

    /**
     * @desc   - Format after a paste
     * @access - public
     * @param  - event : clipboard event from the field
     * @return -
     */
    @HostListener('paste', ['$event'])
    onPaste
    (
        event: ClipboardEvent
    ): void
    {
        setTimeout(() =>
        {
            this.applyFormatting(
                event.target as HTMLInputElement,
                false
            );
        }, 0);
    }
}

export { CurrencyInputDirective };
